import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Checkbox, Label, TextInput, ToggleSwitch } from 'flowbite-react';
import { Ability, Button, Loader, MultiSelect, Select } from '../../components';
import type { Value } from '../../components';
import { Project, User } from '../../models';
import { Boards, Rt } from '../../services';
import type { RtImportBatch, RtImportResult, RtMeta, RtQueue, RtTicket, RtUser } from '../../services/Rt';
import {
    RootState,
    loadProject,
    loadProjects,
    loadUsers,
    useAppDispatch,
    useAppSelector
} from '../../redux';

type BoardOption = {
    _id: string;
    name: string;
    columns: { _id: string; title: string; isSmart?: boolean }[];
};

const firstManualColumn = (board?: BoardOption | null) =>
    board?.columns.find(c => !c.isSmart) || null;

const boardsWithManualColumns = (boards: BoardOption[]) =>
    boards.filter(board => board.columns.some(column => !column.isSmart));

type SortColumn = 'id' | 'subject' | 'status' | 'owner' | 'requestor' | 'imported';
type SortDirection = 'asc' | 'desc';

const PER_PAGE = 50;
const BATCH_POLL_MS = 1500;
const ACTIVE_BATCH_STORAGE_KEY = 'yoda.rtImport.activeBatchId';
const BATCH_SNAPSHOT_STORAGE_KEY = 'yoda.rtImport.batchSnapshot';
const SEARCH_FILTERS_STORAGE_KEY = 'yoda.rtImport.searchFilters';

type SearchFilters = {
    ticketId: string;
    requestor: string;
    owners: string[];
    statuses: string[];
    subject: string;
    queues: string[];
    query: string;
};

const DEFAULT_SEARCH_FILTERS: SearchFilters = {
    ticketId: '',
    requestor: '',
    owners: [],
    statuses: [],
    subject: '',
    queues: [],
    query: ''
};

const isBatchActive = (status?: string | null) =>
    status === 'pending' || status === 'running';

const readStoredBatchId = (): string | null => {
    try {
        return localStorage.getItem(ACTIVE_BATCH_STORAGE_KEY)
            || sessionStorage.getItem(ACTIVE_BATCH_STORAGE_KEY);
    } catch {
        return null;
    }
};

const storeBatchId = (batchId: string | null) => {
    try {
        if (batchId) {
            localStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, batchId);
            sessionStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, batchId);
        } else {
            localStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
            sessionStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
        }
    } catch {
        // private mode / denied — ignore
    }
};

const readStoredBatchSnapshot = (): RtImportBatch | null => {
    try {
        const raw = localStorage.getItem(BATCH_SNAPSHOT_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed?._id || !isBatchActive(parsed.status))
            return null;
        return parsed as RtImportBatch;
    } catch {
        return null;
    }
};

const storeBatchSnapshot = (batch: RtImportBatch | null) => {
    try {
        if (batch && isBatchActive(batch.status)) {
            localStorage.setItem(BATCH_SNAPSHOT_STORAGE_KEY, JSON.stringify({
                _id: batch._id,
                status: batch.status,
                total: batch.total,
                done: batch.done,
                failed: batch.failed,
                projectId: batch.projectId,
                tickets: batch.tickets || [],
                results: batch.results || [],
                startedAt: batch.startedAt,
                finishedAt: batch.finishedAt,
                createdAt: batch.createdAt,
                updatedAt: batch.updatedAt
            }));
            storeBatchId(batch._id);
        } else {
            localStorage.removeItem(BATCH_SNAPSHOT_STORAGE_KEY);
            storeBatchId(null);
        }
    } catch {
        // private mode / denied — ignore
    }
};

const asStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value))
        return [];
    return value.map(entry => String(entry).trim()).filter(Boolean);
};

const readStoredSearchFilters = (): SearchFilters => {
    try {
        const raw = localStorage.getItem(SEARCH_FILTERS_STORAGE_KEY);
        if (!raw)
            return { ...DEFAULT_SEARCH_FILTERS };
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return { ...DEFAULT_SEARCH_FILTERS };
        return {
            ticketId: String(parsed.ticketId || ''),
            requestor: String(parsed.requestor || ''),
            owners: asStringArray(parsed.owners),
            statuses: asStringArray(parsed.statuses),
            subject: String(parsed.subject || ''),
            queues: asStringArray(parsed.queues),
            query: String(parsed.query || '')
        };
    } catch {
        return { ...DEFAULT_SEARCH_FILTERS };
    }
};

const storeSearchFilters = (filters: SearchFilters) => {
    try {
        localStorage.setItem(SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
        // private mode / denied — ignore
    }
};

const SortableHeader = ({
    label,
    column,
    sortColumn,
    sortDirection,
    onSort
}: {
    label: string;
    column: SortColumn;
    sortColumn: SortColumn;
    sortDirection: SortDirection;
    onSort: (column: SortColumn) => void;
}) => (
    <th className="p-3">
        <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-left hover:text-blue-600"
            onClick={() => onSort(column)}
        >
            {label}
            {sortColumn === column && (
                <span className="text-xs text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
            )}
        </button>
    </th>
);

/**
 * Localize multilingual board names (`{ fr, en, de }`) for Select labels.
 */
const localizeLabel = (value: unknown, lang: string): string => {
    if (value == null)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'object') {
        const map = value as Record<string, string>;
        return map[lang] || map.fr || map.en || map.de || Object.values(map).find(Boolean) || '';
    }
    return String(value);
};

const displayCell = (value: unknown) => {
    if (value == null || value === '')
        return '—';
    if (typeof value === 'string' || typeof value === 'number')
        return String(value);
    return '—';
};

/**
 * Admin page: search RT tickets, select, import/re-sync into Yoda.
 */
function RtImportView() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state: RootState) => state.auth);
    const { projects, project } = useAppSelector((state: RootState) => state.projects);
    const { users } = useAppSelector((state: RootState) => state.users);

    const [meta, setMeta] = useState<RtMeta | null>(null);
    const [metaError, setMetaError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [rtUsers, setRtUsers] = useState<RtUser[]>([]);
    const [rtUsersError, setRtUsersError] = useState<string | null>(null);
    const [rtQueues, setRtQueues] = useState<RtQueue[]>([]);
    const [rtQueuesError, setRtQueuesError] = useState<string | null>(null);

    const [filters, setFilters] = useState<SearchFilters>(() => readStoredSearchFilters());
    const [tickets, setTickets] = useState<RtTicket[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [searchError, setSearchError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [sortColumn, setSortColumn] = useState<SortColumn>('id');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const [projectId, setProjectId] = useState<string>('');
    const [boards, setBoards] = useState<BoardOption[]>([]);
    const [boardId, setBoardId] = useState<string>('');
    const [columnId, setColumnId] = useState<string>('');
    const [assigneeId, setAssigneeId] = useState<string>('');
    const [notifyRt, setNotifyRt] = useState(true);
    const [rtStatus, setRtStatus] = useState<string>('');
    const [reSync, setReSync] = useState(false);
    const [results, setResults] = useState<RtImportResult[] | null>(null);
    const [importBatch, setImportBatch] = useState<RtImportBatch | null>(() => readStoredBatchSnapshot());

    const isStaff = user?.role !== User.ROLE_CLIENT;
    const lang = i18n.language?.split('-')[0] || 'fr';
    const activeBatchId = isBatchActive(importBatch?.status) ? importBatch!._id : null;

    const applyImportBatch = useCallback((batch: RtImportBatch | null) => {
        setImportBatch(batch);
        storeBatchSnapshot(batch);
        if (batch?.results?.length)
            setResults(batch.results);
        if (batch && isBatchActive(batch.status))
            setImporting(true);
        else if (batch && !isBatchActive(batch.status))
            setImporting(false);
    }, []);

    useEffect(() => {
        storeSearchFilters(filters);
    }, [filters]);

    useEffect(() => {
        dispatch(loadProjects({}));
        dispatch(loadUsers());
    }, [dispatch]);

    useEffect(() => {
        let cancelled = false;
        Rt.meta()
            .then((data) => {
                if (cancelled) return;
                setMeta(data);
                setRtStatus(data.default_post_import_status || '');
                if (data.active_import_batch && isBatchActive(data.active_import_batch.status)) {
                    applyImportBatch(data.active_import_batch);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setMetaError(err?.message || String(err));
            });

        Rt.listUsers({ per_page: 200 })
            .then((data) => {
                if (cancelled) return;
                setRtUsers(data.items || []);
                setRtUsersError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setRtUsers([]);
                setRtUsersError(err?.message || String(err));
            });

        Rt.listQueues()
            .then((data) => {
                if (cancelled) return;
                setRtQueues(data.items || []);
                setRtQueuesError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setRtQueues([]);
                setRtQueuesError(err?.message || String(err));
            });

        return () => { cancelled = true; };
    }, [applyImportBatch]);

    // Resume after soft navigation AND hard reload (wait for auth — Ctrl+R race).
    useEffect(() => {
        if (!user?._id || !isStaff)
            return undefined;

        let cancelled = false;

        const resume = async () => {
            // 1) Prefer server "active" batch (independent try — must not block local fallback).
            try {
                const active = await Rt.getActiveImportBatch();
                if (cancelled)
                    return;
                if (active.batch && isBatchActive(active.batch.status)) {
                    applyImportBatch(active.batch);
                    return;
                }
            } catch {
                // keep going — local snapshot / id fallback
            }

            // 2) Poll by stored id (localStorage / sessionStorage / snapshot).
            const storedId = readStoredBatchId() || readStoredBatchSnapshot()?._id || null;
            if (!storedId) {
                storeBatchSnapshot(null);
                return;
            }

            try {
                const { batch } = await Rt.getImportBatch(storedId);
                if (cancelled)
                    return;
                applyImportBatch(batch);
                if (!isBatchActive(batch.status))
                    storeBatchSnapshot(null);
            } catch {
                // Keep local snapshot visible if API is temporarily unavailable.
            }
        };

        void resume();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resume once per auth identity
    }, [user?._id, isStaff, applyImportBatch]);

    useEffect(() => {
        if (!projectId) {
            setBoards([]);
            setBoardId('');
            setColumnId('');
            return;
        }
        let cancelled = false;
        Boards.list({ project: projectId })
            .then((payload: any) => {
                if (cancelled) return;
                const list = (payload?.boards || payload || []).map((b: any) => ({
                    _id: b._id,
                    name: localizeLabel(b.name, lang),
                    columns: (b.columns || []).map((c: any) => ({
                        _id: c._id,
                        title: localizeLabel(c.title, lang),
                        isSmart: !!c.isSmart
                    }))
                }));
                const eligible = boardsWithManualColumns(list);
                setBoards(eligible);
                const first = eligible[0];
                setBoardId(first?._id || '');
                setColumnId(firstManualColumn(first)?._id || '');
            })
            .catch(() => {
                if (cancelled) return;
                setBoards([]);
            });
        return () => { cancelled = true; };
    }, [projectId, lang]);

    useEffect(() => {
        setAssigneeId('');
        if (projectId)
            dispatch(loadProject(projectId));
    }, [dispatch, projectId]);

    const selectedProject = useMemo(() => {
        if (project?._id === projectId)
            return project;
        return (projects || []).find((p: Project) => p._id === projectId) || null;
    }, [project, projects, projectId]);

    const selectedBoard = useMemo(
        () => boards.find(b => b._id === boardId) || null,
        [boards, boardId]
    );

    const assigneeOptions = useMemo(() => {
        const pool = (users || []).filter((u: User) => u.role !== User.ROLE_CLIENT);
        const members = selectedProject
            ? pool.filter((u: User) => selectedProject.hasMember(u))
            : [];
        // Prefer project members; if membership data is missing, fall back to all staff.
        const choices = members.length ? members : (selectedProject ? pool : []);
        return [
            { value: '', label: t('rt_import.target.assignee_none') as string },
            ...choices.map((u: User) => ({
                value: u._id,
                label: `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.email || u._id
            }))
        ];
    }, [users, selectedProject, t]);

    const statusOptions = useMemo(() => {
        const statuses = meta?.statuses || [];
        return [
            { value: '', label: t('rt_import.status_unchanged') as string },
            ...statuses.map(s => ({ value: s, label: s }))
        ];
    }, [meta, t]);

    const filterStatusOptions = useMemo(() => (
        (meta?.statuses || []).map(s => ({ value: s, label: s }))
    ), [meta]);

    const ownerOptions = useMemo(() => (
        rtUsers.map(u => ({
            value: u.id,
            label: u.email ? `${u.name} (${u.id})` : `${u.name || u.id}`
        }))
    ), [rtUsers]);

    const queueOptions = useMemo(() => (
        rtQueues.map(q => ({
            value: q.name,
            label: q.description ? `${q.name} — ${q.description}` : q.name
        }))
    ), [rtQueues]);

    const projectOptions = useMemo(
        () => (projects || []).map((p: Project) => ({
            value: p._id,
            label: localizeLabel(p.name, lang)
        })),
        [projects, lang]
    );

    const boardOptions = useMemo(
        () => boards.map(b => ({ value: b._id, label: b.name })),
        [boards]
    );

    const columnOptions = useMemo(
        () => (selectedBoard?.columns || [])
            .filter(c => !c.isSmart)
            .map(c => ({ value: c._id, label: c.title })),
        [selectedBoard]
    );

    const selectedIds = useMemo(
        () => Object.keys(selected).filter(id => selected[id]),
        [selected]
    );

    const displayedTickets = useMemo(() => {
        if (sortColumn !== 'imported' && sortColumn !== 'requestor')
            return tickets;
        return [...tickets].sort((a, b) => {
            let av: string | number = '';
            let bv: string | number = '';
            if (sortColumn === 'imported') {
                av = a.imported ? 1 : 0;
                bv = b.imported ? 1 : 0;
            } else {
                av = String(a.requestor || '').toLowerCase();
                bv = String(b.requestor || '').toLowerCase();
            }
            if (av < bv)
                return sortDirection === 'asc' ? -1 : 1;
            if (av > bv)
                return sortDirection === 'asc' ? 1 : -1;
            return Number(a.id) - Number(b.id);
        });
    }, [tickets, sortColumn, sortDirection]);

    const pageFrom = total ? ((page - 1) * PER_PAGE) + 1 : 0;
    const pageTo = total ? Math.min(page * PER_PAGE, total) : 0;

    const fetchTickets = useCallback(async (overrides?: {
        page?: number;
        sortColumn?: SortColumn;
        sortDirection?: SortDirection;
    }) => {
        const targetPage = overrides?.page ?? page;
        const targetSortColumn = overrides?.sortColumn ?? sortColumn;
        const targetSortDirection = overrides?.sortDirection ?? sortDirection;

        setLoading(true);
        setSearchError(null);
        setResults(null);
        try {
            const params: Record<string, unknown> = {
                id: filters.ticketId.trim() || undefined,
                requestor: filters.requestor || undefined,
                owner: filters.owners.length ? filters.owners : undefined,
                status: filters.statuses.length ? filters.statuses : undefined,
                subject: filters.subject || undefined,
                queue: filters.queues.length ? filters.queues : undefined,
                query: filters.query || undefined,
                page: targetPage,
                per_page: PER_PAGE
            };
            if (targetSortColumn !== 'imported' && targetSortColumn !== 'requestor') {
                params.orderby = targetSortColumn;
                params.order = targetSortDirection === 'asc' ? 'ASC' : 'DESC';
            }

            const data = await Rt.searchTickets(params);
            setTickets(data.items || []);
            setTotal(data.total || 0);
            setPages(data.pages || Math.max(1, Math.ceil((data.total || 0) / PER_PAGE)));
            setPage(data.page || targetPage);
            setSelected({});
        } catch (err: any) {
            setSearchError(err?.message || String(err));
            setTickets([]);
            setTotal(0);
            setPages(1);
        } finally {
            setLoading(false);
        }
    }, [filters, page, sortColumn, sortDirection]);

    const search = useCallback(() => {
        setPage(1);
        void fetchTickets({ page: 1 });
    }, [fetchTickets]);

    const changePage = useCallback((nextPage: number) => {
        setPage(nextPage);
        void fetchTickets({ page: nextPage });
    }, [fetchTickets]);

    const handleSort = useCallback((column: SortColumn) => {
        const nextDirection: SortDirection =
            sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortColumn(column);
        setSortDirection(nextDirection);
        if (column !== 'imported' && column !== 'requestor') {
            setPage(1);
            void fetchTickets({ page: 1, sortColumn: column, sortDirection: nextDirection });
        }
    }, [fetchTickets, sortColumn, sortDirection]);

    const toggleAll = (checked: boolean) => {
        const next: Record<string, boolean> = {};
        displayedTickets.forEach(ticket => { next[ticket.id] = checked; });
        setSelected(next);
    };

    useEffect(() => {
        if (!activeBatchId)
            return undefined;

        let cancelled = false;
        setImporting(true);
        storeBatchId(activeBatchId);

        const poll = async () => {
            while (!cancelled) {
                await new Promise(resolve => setTimeout(resolve, BATCH_POLL_MS));
                if (cancelled)
                    return;
                try {
                    const { batch } = await Rt.getImportBatch(activeBatchId);
                    if (cancelled)
                        return;
                    applyImportBatch(batch);
                    if (!isBatchActive(batch.status)) {
                        storeBatchSnapshot(null);
                        setImporting(false);
                        await fetchTickets({ page });
                        return;
                    }
                } catch {
                    if (!cancelled)
                        setImporting(false);
                    return;
                }
            }
        };

        void poll();
        return () => { cancelled = true; };
    }, [activeBatchId, applyImportBatch, fetchTickets, page]);

    const runImport = async () => {
        if (!projectId || !selectedIds.length || activeBatchId)
            return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setImporting(true);
        setResults(null);
        try {
            const data = await Rt.importTickets({
                ticketIds: selectedIds,
                projectId,
                boardId: boardId || null,
                columnId: columnId || null,
                assigneeId: assigneeId || null,
                notifyRt,
                rtStatus: rtStatus || null,
                reSync
            });
            applyImportBatch(data.batch);
            // Polling continues via activeBatchId effect.
        } catch (err: any) {
            if (err?.status === 409) {
                try {
                    const active = await Rt.getActiveImportBatch();
                    if (active.batch) {
                        applyImportBatch(active.batch);
                        return;
                    }
                } catch {
                    // fall through to conflict message
                }
                setResults([{
                    ticketId: '-',
                    mode: 'error',
                    error: t('rt_import.batch_conflict') as string
                }]);
            } else {
                setResults([{
                    ticketId: '-',
                    mode: 'error',
                    error: err?.message || String(err)
                }]);
            }
            setImporting(false);
        }
    };

    if (!isStaff)
        return <div className="p-6 text-sm text-gray-600 dark:text-gray-300">{t('rt_import.forbidden')}</div>;

    if (metaError)
        return (
            <div className="p-6 space-y-2">
                <h1 className="text-xl font-semibold">{t('rt_import.title')}</h1>
                <p className="text-sm text-red-600">{t('rt_import.unavailable')}: {metaError}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('rt_import.enable_hint')}</p>
            </div>
        );

    const progressSection = importBatch ? (
        <section className="bg-white dark:bg-gray-800 border rounded-lg p-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-medium">
                    {isBatchActive(importBatch.status)
                        ? t('rt_import.batch_running')
                        : importBatch.status === 'failed'
                            ? t('rt_import.batch_failed')
                            : t('rt_import.batch_completed')}
                </h2>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                    {t('rt_import.import_progress', {
                        done: importBatch.done,
                        total: importBatch.total
                    })}
                    {importBatch.failed > 0 && (
                        <> — {t('rt_import.import_failed_count', { count: importBatch.failed })}</>
                    )}
                </span>
            </div>
            <div className="h-2 w-full rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div
                    className={`h-full transition-all ${
                        importBatch.status === 'failed' ? 'bg-amber-500' : 'bg-blue-600'
                    }`}
                    style={{
                        width: `${importBatch.total
                            ? Math.min(100, Math.round((importBatch.done / importBatch.total) * 100))
                            : 0}%`
                    }}
                />
            </div>
        </section>
    ) : null;

    const displayResults = (results?.length
        ? results
        : (importBatch?.results || [])) as RtImportResult[];

    const modeLabel = (mode: string) => {
        const key = `rt_import.modes.${mode}`;
        const translated = t(key);
        return translated === key ? mode : translated;
    };

    const reasonLabel = (reason?: string) => {
        if (!reason)
            return null;
        const key = `rt_import.reasons.${reason}`;
        const translated = t(key);
        return translated === key ? reason : translated;
    };

    const resultsSection = importBatch && displayResults.length > 0 ? (
        <section className="bg-white dark:bg-gray-800 border rounded-lg p-4 space-y-2">
            <h2 className="font-medium">{t('rt_import.results')}</h2>
            <ul className="text-sm space-y-1 max-h-64 overflow-y-auto">
                {displayResults.map((r, idx) => (
                    <li key={`${r.ticketId}-${idx}`}>
                        RT #{r.ticketId}: <strong>{modeLabel(r.mode)}</strong>
                        {r.change?.url && (
                            <> — <a className="text-blue-600" href={r.change.url} target="_blank" rel="noreferrer">
                                Yoda #{r.change.slug}
                            </a></>
                        )}
                        {r.reason && <> ({reasonLabel(r.reason)})</>}
                        {r.error && <span className="text-red-600"> — {r.error}</span>}
                        {r.rtCommentFailed && (
                            <span className="text-amber-600"> — {t('rt_import.rt_comment_failed')}</span>
                        )}
                        {r.rtStatusFailed && (
                            <span className="text-amber-600"> — {t('rt_import.rt_status_failed')}</span>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    ) : null;

    if (!meta)
        return (
            <div className="p-6 space-y-4 max-w-6xl mx-auto">
                {progressSection}
                {resultsSection}
                <Loader />
            </div>
        );

    return (
        <div className="p-6 space-y-6 max-w-6xl mx-auto">
            <div>
                <h1 className="text-xl font-semibold">{t('rt_import.title')}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('rt_import.subtitle')}</p>
            </div>

            {progressSection}
            {resultsSection}

            <section className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white dark:bg-gray-800 border rounded-lg p-4">
                <div>
                    <Label htmlFor="rt-ticket-id">{t('rt_import.filters.ticket_id')}</Label>
                    <TextInput
                        id="rt-ticket-id"
                        type="number"
                        min={1}
                        value={filters.ticketId}
                        onChange={e => setFilters(f => ({ ...f, ticketId: e.target.value }))}
                        placeholder="433406"
                    />
                </div>
                <div>
                    <Label htmlFor="rt-requestor">{t('rt_import.filters.requestor')}</Label>
                    <TextInput id="rt-requestor" value={filters.requestor}
                        onChange={e => setFilters(f => ({ ...f, requestor: e.target.value }))} />
                </div>
                <div>
                    <Label>{t('rt_import.filters.owner')}</Label>
                    <MultiSelect
                        defaultValue={filters.owners}
                        onChange={(values: Value[]) => setFilters(f => ({
                            ...f,
                            owners: values.map(String)
                        }))}
                        options={ownerOptions}
                        placeholder={t('rt_import.filters.owner_any') as string}
                    />
                    {rtUsersError && (
                        <p className="mt-1 text-xs text-amber-600">{rtUsersError}</p>
                    )}
                </div>
                <div>
                    <Label>{t('rt_import.filters.status')}</Label>
                    <MultiSelect
                        defaultValue={filters.statuses}
                        onChange={(values: Value[]) => setFilters(f => ({
                            ...f,
                            statuses: values.map(String)
                        }))}
                        options={filterStatusOptions}
                        placeholder={t('rt_import.filters.status_any') as string}
                    />
                </div>
                <div>
                    <Label htmlFor="rt-subject">{t('rt_import.filters.subject')}</Label>
                    <TextInput id="rt-subject" value={filters.subject}
                        onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))} />
                </div>
                <div>
                    <Label>{t('rt_import.filters.queue')}</Label>
                    <MultiSelect
                        defaultValue={filters.queues}
                        onChange={(values: Value[]) => setFilters(f => ({
                            ...f,
                            queues: values.map(String)
                        }))}
                        options={queueOptions}
                        placeholder={t('rt_import.filters.queue_any') as string}
                    />
                    {rtQueuesError && (
                        <p className="mt-1 text-xs text-amber-600">{rtQueuesError}</p>
                    )}
                </div>
                <div className="md:col-span-3">
                    <Label htmlFor="rt-query">{t('rt_import.filters.query')}</Label>
                    <TextInput id="rt-query" value={filters.query}
                        onChange={e => setFilters(f => ({ ...f, query: e.target.value }))}
                        placeholder="Owner = 'balidra' AND Status = 'open'"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('rt_import.filters.query_help')}</p>
                    <p className="text-xs text-gray-400">{t('rt_import.filters.query_examples')}</p>
                </div>
                <div className="md:col-span-3 flex gap-2">
                    <Button
                        title={loading ? t('rt_import.searching') : t('rt_import.search')}
                        onClick={search}
                        loading={loading}
                        color="primary"
                    />
                </div>
                {searchError && (
                    <p className="md:col-span-3 text-sm text-red-600">{searchError}</p>
                )}
            </section>

            <section className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                            <tr>
                                <th className="p-3">
                                    <Checkbox
                                        checked={displayedTickets.length > 0
                                            && selectedIds.length === displayedTickets.length}
                                        onChange={e => toggleAll(e.target.checked)}
                                    />
                                </th>
                                <SortableHeader
                                    label="ID"
                                    column="id"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label={t('rt_import.table.subject') as string}
                                    column="subject"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label={t('rt_import.table.status') as string}
                                    column="status"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label={t('rt_import.table.owner') as string}
                                    column="owner"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label={t('rt_import.table.requestor') as string}
                                    column="requestor"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label={t('rt_import.table.imported') as string}
                                    column="imported"
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                            </tr>
                        </thead>
                        <tbody>
                            {displayedTickets.map(ticket => (
                                <tr key={ticket.id} className="border-t">
                                    <td className="p-3">
                                        <Checkbox
                                            checked={!!selected[ticket.id]}
                                            onChange={e => setSelected(s => ({
                                                ...s,
                                                [ticket.id]: e.target.checked
                                            }))}
                                        />
                                    </td>
                                    <td className="p-3 font-mono">
                                        {ticket.link ? (
                                            <a href={ticket.link} target="_blank" rel="noreferrer" className="text-blue-600">
                                                #{ticket.id}
                                            </a>
                                        ) : `#${ticket.id}`}
                                    </td>
                                    <td className="p-3">{displayCell(ticket.subject)}</td>
                                    <td className="p-3">{displayCell(ticket.status)}</td>
                                    <td className="p-3">{displayCell(ticket.owner)}</td>
                                    <td className="p-3">{displayCell(ticket.requestor)}</td>
                                    <td className="p-3">
                                        {ticket.imported && ticket.change ? (
                                            <Link
                                                className="text-blue-600"
                                                to={`/projects/${ticket.change.project}/change/${ticket.change._id}`}
                                            >
                                                #{ticket.change.slug}
                                            </Link>
                                        ) : '—'}
                                    </td>
                                </tr>
                            ))}
                            {!displayedTickets.length && (
                                <tr>
                                    <td colSpan={7} className="p-4 text-gray-500 dark:text-gray-400">
                                        {t('rt_import.table.empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {total > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        <span>
                            {t('rt_import.table.page_info', {
                                from: pageFrom,
                                to: pageTo,
                                total
                            })}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="rounded-md border px-3 py-1.5 disabled:opacity-40"
                                disabled={page <= 1 || loading}
                                onClick={() => changePage(page - 1)}
                            >
                                {t('rt_import.table.prev')}
                            </button>
                            <span>
                                {t('rt_import.table.page_count', { page, pages })}
                            </span>
                            <button
                                type="button"
                                className="rounded-md border px-3 py-1.5 disabled:opacity-40"
                                disabled={page >= pages || loading}
                                onClick={() => changePage(page + 1)}
                            >
                                {t('rt_import.table.next')}
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {!isBatchActive(importBatch?.status) && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800 border rounded-lg p-4">
                <div>
                    <Label>{t('rt_import.target.project')}</Label>
                    <Select
                        defaultValue={projectId}
                        onChange={(v: Value) => setProjectId(String(v))}
                        options={projectOptions}
                        placeholder={t('rt_import.target.project_placeholder') as string}
                    />
                </div>
                <div>
                    <Label>{t('rt_import.target.board')}</Label>
                    <Select
                        defaultValue={boardId}
                        onChange={(v: Value) => {
                            const next = String(v);
                            setBoardId(next);
                            const board = boards.find(b => b._id === next);
                            setColumnId(firstManualColumn(board)?._id || '');
                        }}
                        options={boardOptions}
                        placeholder={t('rt_import.target.board') as string}
                    />
                </div>
                <div>
                    <Label>{t('rt_import.target.column')}</Label>
                    <Select
                        defaultValue={columnId}
                        onChange={(v: Value) => setColumnId(String(v))}
                        options={columnOptions}
                        placeholder={t('rt_import.target.column') as string}
                    />
                </div>
                <div>
                    <Label>{t('rt_import.target.assignee')}</Label>
                    <Select
                        defaultValue={assigneeId}
                        onChange={(v: Value) => setAssigneeId(String(v))}
                        options={assigneeOptions}
                        placeholder={t('rt_import.target.assignee_none') as string}
                    />
                </div>
                <div className="flex items-center gap-3">
                    <ToggleSwitch checked={notifyRt} label={t('rt_import.target.notify_rt') as string} onChange={setNotifyRt} />
                </div>
                <div>
                    <Label>{t('rt_import.target.rt_status')}</Label>
                    <Select
                        defaultValue={rtStatus}
                        onChange={(v: Value) => setRtStatus(String(v))}
                        options={statusOptions}
                    />
                </div>
                <div className="flex items-center gap-3">
                    <ToggleSwitch checked={reSync} label={t('rt_import.target.resync') as string} onChange={setReSync} />
                </div>
                <div className="md:col-span-2">
                    <Ability can="change:create">
                        <Button
                            title={importing
                                ? t('rt_import.importing')
                                : t('rt_import.import', { count: selectedIds.length })}
                            onClick={runImport}
                            loading={importing}
                            disabled={!selectedIds.length || !projectId}
                            color="primary"
                        />
                    </Ability>
                </div>
            </section>
            )}
        </div>
    );
}

export default RtImportView;
